import { ListItem, ListItemText } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { type GeoLocation } from "@src/appV2/Location/types";

import { MapWithAddress } from "../GeoLocation/MapWithAddress";
import { WorkplaceHighlightPills } from "../Workplace/Highlights/Pills";
import { useWorkplaceHighlights } from "../Workplace/Highlights/useWorkplaceHighlights";
import { QuestionTag } from "../Workplace/Review/types";

interface ShiftHighlightsAndMapListItemProps {
  workplaceId: string;
  workplaceName: string;
  workplaceLocation: GeoLocation;
  workplaceFormattedAddress?: string;
  isDividerEnabled?: boolean;
  isMapEnabled?: boolean;
}

/**
 * When there are fewer than 5 highlights, display each one on its own row to improve visual balance.
 * This prevents the card from looking too empty with sparse content.
 */
const HIGHLIGHTS_WRAP_THRESHOLD = 5;

export function ShiftHighlightsAndMapListItem(props: ShiftHighlightsAndMapListItemProps) {
  const {
    workplaceId,
    workplaceName,
    workplaceLocation,
    workplaceFormattedAddress,
    isDividerEnabled = true,
    isMapEnabled = true,
  } = props;

  const { highlights, isLoading: isHighlightsLoading } =
    // Hide patient:nurse ratios from the shift card highlights, reason: https://linear.app/clipboardhealth/issue/ACT-1871
    useWorkplaceHighlights({ workplaceId, excludeList: [QuestionTag.PATIENTS_PER_NURSE] });

  return (
    <ListItem divider={isDividerEnabled} sx={{ paddingTop: 0 }}>
      <ListItemText
        disableTypography
        sx={{ paddingLeft: "2rem" }}
        primary={
          <Stack spacing={4}>
            <WorkplaceHighlightPills
              highlights={highlights}
              isLoading={isHighlightsLoading}
              wrapperSx={(theme) => ({
                marginTop: `calc(-1 * ${theme.spacing(3)})`,
                maxWidth: highlights.length > HIGHLIGHTS_WRAP_THRESHOLD ? "100%" : "min-content",
              })}
            />
            {isMapEnabled && (
              <Box sx={{ height: "13rem", width: "100%" }}>
                <MapWithAddress
                  readonly
                  workplaceId={workplaceId}
                  workplaceName={workplaceName}
                  workplaceFormattedAddress={workplaceFormattedAddress}
                  location={{
                    lat: workplaceLocation.latitude,
                    lng: workplaceLocation.longitude,
                  }}
                />
              </Box>
            )}
          </Stack>
        }
      />
    </ListItem>
  );
}
